<script>
export default {
  name: "ViewFulfillmentReports",
};
</script>

<script setup>
import { ref, reactive } from "vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import TableauEventDetail from "@/components/StudentFulfillment/components/report/TableauEventDetail";
import ReportContainer from "@/components/shared/ReportContainer";
import { useWindowSize } from "@vueuse/core";
import { useToast } from "vue-toast-notification";
import { validate as validateUUID } from "uuid";
import { datadogLogs } from "@datadog/browser-logs";
import {
  ordersReportSource,
  ordersMiscellaneousProductsReportSource,
  ordersActualHousingBookingsReportSource,
  ordersHousingInternationalReportSource,
  ordersExcursionsInternationalReportSource,
} from "@/composables/tableau";
import {
  hasOrdersReadPermission,
  hasOrdersWritePermission,
} from "@/composables/authorization";
import { TABLEAU_FIELD_NAMES } from "../constants";
import { ERROR_TIMEOUT } from "@/constants.js";

const toast = useToast();

const bedID = ref("");
const modalIsOpen = ref(false);
const actualHousingBookingsReportRef = ref();
const tableauInformation = reactive({
  columns: [],
  data: [],
  extra: {},
});
const { width } = useWindowSize();
const height = 1800;

const roomHeaders = ["Actual Unit Name", "Room ID", "Room", "Bed ID"];
const bedIdHeaderName = "Bed ID";
const tabPanelStyles = {
  header: () => ({
    class: ["w-full"],
  }),
  headerAction: "justify-center",
  content: "p-1",
};

const onMarksSelectionBookingsReport = async (marksEvent) => {
  const filtersArray = await actualHousingBookingsReportRef.value?.executeGetFilters();
  filtersArray?.forEach((selectedFilter) => {
    // Updates tableauInformation if Booking Start Date got changed
    if (
      selectedFilter?.fieldName === TABLEAU_FIELD_NAMES.booking_start_date &&
      selectedFilter?.minValue?.value
    ) {
      tableauInformation.extra.booking_start_date =
        selectedFilter.minValue.value;
    }

    // Updates tableauInformation if Booking End Date got changed
    if (
      selectedFilter?.fieldName === TABLEAU_FIELD_NAMES.booking_end_date &&
      selectedFilter?.maxValue?.value
    ) {
      tableauInformation.extra.booking_end_date = selectedFilter.maxValue.value;
    }
  });
  await onMarksSelection(marksEvent);
};

const onMarksSelection = async (marksEvent) => {
  const marks = await marksEvent.detail.getMarksAsync();

  marks.data.forEach((markDatum) => {
    const columns = markDatum?.columns.filter((column) =>
      roomHeaders.includes(column.fieldName)
    );
    const bedIdIndex = columns.findIndex(
      (column) => column?._fieldName === bedIdHeaderName
    );
    const columnIndexes = columns.map((column) => column?._index);
    if (markDatum.data[0] && bedIdIndex > -1) {
      markDatum.data[0] = markDatum.data[0].filter((_, idx) =>
        columnIndexes.includes(idx)
      );
      bedID.value = markDatum?.data?.[0]?.[bedIdIndex]?.value;
    }

    const data = markDatum?.data;

    if (columns?.length && data?.length) {
      if (validateUUID(bedID.value)) {
        tableauInformation.columns =
          columns?.map((column) => column?.fieldName) ?? [];

        tableauInformation.data =
          data?.map(
            (mark) =>
              mark?.map((item) => item?.formattedValue ?? item?.value) ?? []
          ) ?? [];

        modalIsOpen.value = true;
      } else {
        datadogLogs.logger.warn(
          "Attempting to get bed_id that has invalid uuid",
          { data: markDatum?.data }
        );
        toast.open({
          message: "Error while trying to open Booking modal: No Bed Id found!",
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
    }
  });
};
</script>

<template>
  <div class="bg-blue-100 text-sm text-indigo-base w-full overflow-x-hidden">
    <TableauEventDetail
      :bed-id="bedID"
      :modal-is-open="modalIsOpen"
      :tableau-information="tableauInformation"
      @close-modal="modalIsOpen = false"
    />
    <div class="mx-2 w-full">
      <div class="flex flex-col items-baseline justify-between">
        <TabView :active-index="0" :lazy="true" class="w-full">
          <TabPanel
            v-if="hasOrdersReadPermission"
            header="Orders Report"
            :pt="tabPanelStyles"
          >
            <ReportContainer
              :src="ordersReportSource"
              :width="width"
              :height="height"
            />
          </TabPanel>
          <TabPanel
            v-if="hasOrdersWritePermission"
            :pt="tabPanelStyles"
            header="Housing International Report"
          >
            <ReportContainer
              :src="ordersHousingInternationalReportSource"
              :width="width"
              :height="height"
            />
          </TabPanel>
          <TabPanel
            v-if="hasOrdersWritePermission"
            :pt="tabPanelStyles"
            header="Actual Housing & Bookings Report"
          >
            <ReportContainer
              ref="actualHousingBookingsReportRef"
              :src="ordersActualHousingBookingsReportSource"
              :on-marks-selection="onMarksSelectionBookingsReport"
              :width="width"
              :height="height"
            />
          </TabPanel>
          <TabPanel
            v-if="hasOrdersWritePermission"
            :pt="tabPanelStyles"
            header="Excursions International Report"
          >
            <ReportContainer
              :src="ordersExcursionsInternationalReportSource"
              :width="width"
              :height="height"
            />
          </TabPanel>
          <TabPanel
            v-if="hasOrdersWritePermission"
            :pt="tabPanelStyles"
            header="Miscellaneous Products Report"
          >
            <ReportContainer
              :src="ordersMiscellaneousProductsReportSource"
              :width="width"
              :height="height"
            />
          </TabPanel>
        </TabView>
      </div>
    </div>
  </div>
</template>

<style scoped>
li[data-pc-section="inkbar"] {
  display: block;
  position: absolute;
  bottom: 0;
}
</style>
